import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "../lib/content";
import { Download, Mail, ExternalLink, Music2, Video, Calendar } from "lucide-react";

const QUICK_LINKS = [
  { label: "SoundCloud", slot: "social.soundcloud_url", fallback: "https://soundcloud.com/sandrita-soundation" },
  { label: "Linktree", slot: "social.linktree_url", fallback: "https://linktr.ee/sandravidalsoundation" },
  { label: "Instagram", slot: "social.instagram_url", fallback: "https://www.instagram.com/" },
  { label: "Bandcamp", slot: "social.bandcamp_url", fallback: "https://bandcamp.com/" },
  { label: "Youtube", slot: "social.youtube_url", fallback: "https://www.youtube.com/" },
];

export default function PressKit() {
  const kicker = useContent("page.press_kit.kicker", "/ 09 — PRESS KIT");
  const title = useContent("page.press_kit.title", "PRESS KIT EPK 2026");
  const intro = useContent(
    "page.press_kit.intro",
    "Everything press, promoters and festival programmers need — in one place. Bio, photos, sets, videos and direct contact."
  );
  const logo = useContent("page.press_kit.logo", "");
  const artistKicker = useContent("page.press_kit.artist_kicker", "/ ARTIST");
  const artistName = useContent("page.press_kit.artist_name", "INNER PULSE");
  const artistSub = useContent("page.press_kit.artist_sub", "AKA Sandrita · Aotearoa / Spain");
  const bioKicker = useContent("page.press_kit.short_bio_kicker", "/ SHORT BIO");
  const shortBio = useContent("page.press_kit.short_bio", "");
  const stat1L = useContent("page.press_kit.stat1_label", "Gigs Played");
  const stat1V = useContent("page.press_kit.stat1_value", "70+");
  const stat2L = useContent("page.press_kit.stat2_label", "Countries");
  const stat2V = useContent("page.press_kit.stat2_value", "NZ · IT");
  const stat3L = useContent("page.press_kit.stat3_label", "Profiles");
  const stat3V = useContent("page.press_kit.stat3_value", "Inner Pulse · Sandrita");
  const cta1Label = useContent("page.press_kit.cta1_label", "Full Media Kit");
  const cta1Url = useContent("page.press_kit.cta1_url", "https://linktr.ee/sandravidalsoundation");
  const cta2Label = useContent("page.press_kit.cta2_label", "Request Tech Rider");
  const cta2Url = useContent("page.press_kit.cta2_url", "/tech-rider");
  const photosTitle = useContent("page.press_kit.photos_title", "Photos");
  const photo1 = useContent("page.press_kit.photo1", "");
  const photo2 = useContent("page.press_kit.photo2", "");
  const photo3 = useContent("page.press_kit.photo3", "");
  const photos = [photo1, photo2, photo3].filter(Boolean);

  const stats = [
    { k: stat1L, v: stat1V },
    { k: stat2L, v: stat2V },
    { k: stat3L, v: stat3V },
  ].filter((s) => s.k && s.v);

  const isInternal = cta2Url.startsWith("/");

  return (
    <div data-testid="press-kit-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="pk-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-2xl mt-8 leading-relaxed">
            {intro}
          </p>
        </div>

        {/* HERO RIBBON */}
        <section
          className="hairline-t pt-10 grid md:grid-cols-12 gap-10 mb-20"
          data-testid="pk-overview"
        >
          <div className="md:col-span-4 flex flex-col items-start gap-6">
            {logo && (
              <img
                src={logo}
                alt="Inner Pulse"
                className="w-full max-w-[260px] aspect-square object-cover rounded-full hairline"
              />
            )}
            <div>
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500 mb-1">
                {artistKicker}
              </div>
              <div className="font-title text-2xl tracking-[0.12em] text-silver-100">
                {artistName}
              </div>
              <div className="font-body text-xs text-silver-500 mt-1">
                {artistSub}
              </div>
            </div>
          </div>

          <div className="md:col-span-8 flex flex-col gap-6">
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
              {bioKicker}
            </div>
            <p className="font-body text-silver-200 text-base md:text-lg leading-relaxed">
              {shortBio}
            </p>
            <div className={`grid grid-cols-${stats.length || 1} md:grid-cols-${stats.length || 1} gap-6 hairline-t pt-6`}>
              {stats.map((s, i) => (
                <div key={i} data-testid={`pk-stat-${i}`}>
                  <div className="font-title text-[0.58rem] tracking-[0.3em] text-silver-600 mb-2">
                    / {s.k}
                  </div>
                  <div className="font-title text-base md:text-lg tracking-[0.1em] text-silver-100">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
              {cta1Label && (
                <a
                  href={cta1Url}
                  target={cta1Url.startsWith("/") ? undefined : "_blank"}
                  rel="noreferrer"
                  className="btn-sharp"
                  data-testid="pk-cta-1"
                >
                  <Download size={14} /> &nbsp; {cta1Label}
                </a>
              )}
              {cta2Label && (
                isInternal ? (
                  <Link to={cta2Url} className="btn-ghost" data-testid="pk-cta-2">
                    {cta2Label} <ExternalLink size={12} />
                  </Link>
                ) : (
                  <a
                    href={cta2Url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost"
                    data-testid="pk-cta-2"
                  >
                    {cta2Label} <ExternalLink size={12} />
                  </a>
                )
              )}
            </div>
          </div>
        </section>

        {/* PHOTOS */}
        {photos.length > 0 && (
          <section className="hairline-t pt-10 mb-20" data-testid="pk-photos">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.12em] text-silver-100">
                {photosTitle}
              </h2>
              <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                / RIGHT-CLICK TO SAVE
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {photos.map((p, i) => (
                <a
                  key={i}
                  href={p}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-[4/5] hairline overflow-hidden block group"
                  data-testid={`pk-photo-${i}`}
                >
                  <img
                    src={p}
                    alt={`Inner Pulse — Press ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* QUICK ACCESS GRID */}
        <section className="hairline-t pt-10 mb-20" data-testid="pk-grid">
          <div className="grid md:grid-cols-3 gap-px bg-silver-800">
            {[
              { Icon: Music2, title: "Sets", desc: "SoundCloud sessions.", to: "/sets" },
              { Icon: Video, title: "Videos", desc: "YouTube + Instagram footage.", to: "/videos" },
              { Icon: Calendar, title: "Events", desc: "70+ shows. 2021–2026.", to: "/events" },
            ].map((c) => (
              <Link
                key={c.title}
                to={c.to}
                className="group bg-black p-8 flex flex-col gap-4 hover:bg-silver-900/40 transition-all duration-500"
                data-testid={`pk-card-${c.title.toLowerCase()}`}
              >
                <c.Icon size={22} className="text-silver-300 group-hover:text-silver-100 transition-colors" />
                <h3 className="font-title text-xl tracking-[0.12em] text-silver-100">
                  {c.title}
                </h3>
                <p className="font-body text-xs text-silver-500 leading-relaxed">
                  {c.desc}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* LINKS + CONTACT */}
        <section
          className="hairline-t pt-10 grid md:grid-cols-12 gap-10"
          data-testid="pk-contact"
        >
          <div className="md:col-span-6">
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
              / LINKS
            </div>
            <ul className="grid grid-cols-2 gap-y-3 gap-x-4">
              {QUICK_LINKS.map((l) => (
                <PkLink key={l.label} link={l} />
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
              / DIRECT CONTACT
            </div>
            <a
              href="mailto:soundationtechnoevents@gmail.com"
              className="inline-flex items-center gap-3 hairline px-6 py-4 hover:bg-silver-900/40 transition-colors"
              data-testid="pk-email"
            >
              <Mail size={16} className="text-silver-300" />
              <span className="font-body text-sm text-silver-100">
                soundationtechnoevents@gmail.com
              </span>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

function PkLink({ link }) {
  const url = useContent(link.slot, link.fallback);
  if (!url) return null;
  return (
    <li>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="font-title text-[0.7rem] tracking-[0.2em] text-silver-300 hover:text-silver-100 transition-colors uppercase flex items-center gap-2"
        data-testid={`pk-link-${link.label.toLowerCase()}`}
      >
        {link.label} <ExternalLink size={12} />
      </a>
    </li>
  );
}
